import type { AbsenceTimeOffBalance } from './model';

export type AbsenceTypeBalance = Omit<AbsenceTimeOffBalance, 'entitlement' | 'timeOffPlan'> & { policyCount: number };

const quantityKeys = ['accrued', 'carryOver', 'granted', 'adjusted', 'used', 'expired', 'balance'] as const;
const scaled = (value: string) => {
  if (!/^-?\d+(?:\.\d{1,4})?$/.test(value)) return undefined;
  const negative = value.startsWith('-');
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  return (BigInt(whole) * 10_000n + BigInt(fraction.padEnd(4, '0'))) * (negative ? -1n : 1n);
};
const decimal = (value: bigint) => {
  const magnitude = value < 0n ? -value : value;
  const fraction = String(magnitude % 10_000n).padStart(4, '0').replace(/0+$/, '');
  return `${value < 0n ? '-' : ''}${magnitude / 10_000n}${fraction ? `.${fraction}` : ''}`;
};

/** 계정별 API 수량을 같은 유형으로 합칩니다. pending은 계정마다 반복되는 유형 전체 수량입니다. */
export function absenceTypeBalance(balances: readonly AbsenceTimeOffBalance[], typeId: string): AbsenceTypeBalance | undefined {
  const matching = balances.filter((balance) => balance.timeOffType.id === typeId);
  const accounts = [...new Map(matching.map((balance) => [balance.entitlement.id, balance])).values()];
  const first = accounts[0];
  if (!first || accounts.some((item) => item.worker.id !== first.worker.id || item.asOf !== first.asOf || item.unitOfTime !== first.unitOfTime)) return undefined;
  const pending = scaled(first.pending);
  if (pending === undefined || accounts.some((item) => scaled(item.pending) !== pending)) return undefined;
  const totals = Object.fromEntries(quantityKeys.map((key) => [key, 0n])) as Record<typeof quantityKeys[number], bigint>;
  let availableBeforePending = 0n;
  for (const account of accounts) {
    for (const key of quantityKeys) {
      const value = scaled(account[key]);
      if (value === undefined) return undefined;
      totals[key] += value;
    }
    const available = scaled(account.available);
    if (available === undefined) return undefined;
    // available은 만료를 직접 반영하므로 raw balance로 다시 계산하지 않습니다.
    availableBeforePending += available + pending;
  }
  return {
    worker: first.worker, timeOffType: first.timeOffType, asOf: first.asOf, unitOfTime: first.unitOfTime,
    ...Object.fromEntries(quantityKeys.map((key) => [key, decimal(totals[key])])) as Pick<AbsenceTypeBalance, typeof quantityKeys[number]>,
    pending: decimal(pending), available: decimal(availableBeforePending - pending),
    policyCount: new Set(accounts.map((account) => account.timeOffPlan.id)).size,
  };
}

/** 유형별 승인 대기를 중복 없이 반영한 뒤, 같은 단위의 잔액만 합칩니다. */
export function absenceBalanceTotals(balances: readonly AbsenceTimeOffBalance[]): {
  totals: Array<Pick<AbsenceTypeBalance, 'unitOfTime' | 'available' | 'pending'>>;
  asOf: string;
} | undefined {
  const first = balances[0];
  if (!first || balances.some((item) => item.worker.id !== first.worker.id || item.asOf !== first.asOf)) return undefined;
  const byType = new Map<string, AbsenceTimeOffBalance[]>();
  for (const balance of balances) {
    const accounts = byType.get(balance.timeOffType.id) ?? [];
    accounts.push(balance);
    byType.set(balance.timeOffType.id, accounts);
  }
  const byUnit = new Map<string, { available: bigint; pending: bigint }>();
  for (const [typeId, accounts] of byType) {
    const balance = absenceTypeBalance(accounts, typeId);
    if (!balance) return undefined;
    const available = scaled(balance.available);
    const pending = scaled(balance.pending);
    if (available === undefined || pending === undefined) return undefined;
    const total = byUnit.get(balance.unitOfTime) ?? { available: 0n, pending: 0n };
    total.available += available;
    total.pending += pending;
    byUnit.set(balance.unitOfTime, total);
  }
  return {
    totals: [...byUnit].map(([unitOfTime, total]) => ({ unitOfTime, available: decimal(total.available), pending: decimal(total.pending) })),
    asOf: first.asOf,
  };
}
